import React, { useState, useEffect } from 'react';
import { db } from '../../../../auth/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { FaTrash, FaCheck, FaEye, FaTimes, FaCalendarAlt, FaUniversity, FaPhone, FaEnvelope, FaFlag, FaBook, FaClock, FaGraduationCap, FaChartLine, FaBoxOpen } from 'react-icons/fa';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { nationalityData } from '../../../../utils/nationality_data';
import './subscription_controls.css';

// # Product name formatting
const formatProductName = (productKey) => {
  const productMap = {
    'step1_library': 'USMLE Step 1 Library',
    'step2_library': 'USMLE Step 2 Library',
    'step1_mentorship': 'Step 1 Mentorship Program',
    'step2_mentorship': 'Step 2 Mentorship Program',
    'OET': 'OET Preparation Course',
    'US_VISA_Counselling': 'US VISA Counseling Service'
  };
  return productMap[productKey] || productKey;
};

// # Main Subscription Controls component
const SubscriptionControls = () => {
  const [activeTab, setActiveTab] = useState('controls');
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestFilter, setRequestFilter] = useState('all');

  // # Helper function to find nationality by name
  const findNationalityByName = (name) => {
    if (!name) return null;
    
    // # Normalize the name for comparison
    const normalizedName = name.toLowerCase().trim();
    
    // # First try direct match with name
    const directMatch = Object.entries(nationalityData).find(
      ([_, data]) => data.name.toLowerCase() === normalizedName
    );
    
    if (directMatch) return directMatch[1];
    
    // # Then try to match with search keys
    const keyMatch = Object.entries(nationalityData).find(
      ([_, data]) => data.searchKeys.some(key => normalizedName.includes(key.toLowerCase()))
    );
    
    if (keyMatch) return keyMatch[1];
    
    // # Try partial matching with name (more flexible)
    const partialNameMatch = Object.entries(nationalityData).find(
      ([_, data]) => data.name.toLowerCase().includes(normalizedName) || 
                     normalizedName.includes(data.name.toLowerCase())
    );
    
    if (partialNameMatch) return partialNameMatch[1];
    
    // # Try matching with the key itself (country code)
    const keyCodeMatch = Object.entries(nationalityData).find(
      ([key, _]) => key.toLowerCase() === normalizedName || 
                    key.toLowerCase().replace('_', ' ') === normalizedName
    );
    
    return keyCodeMatch ? keyCodeMatch[1] : null;
  };

  // # Helper function to filter requests
  const getFilteredRequests = () => {
    switch(requestFilter) {
      case 'pending':
        return requests.filter(req => !req.isRead && req.status !== 'complete');
      case 'read':
        return requests.filter(req => req.isRead && req.status !== 'complete');
      case 'complete':
        return requests.filter(req => req.status === 'complete');
      default:
        return requests;
    }
  };

  // # Get request counts for each filter
  const getRequestCounts = () => {
    const pending = requests.filter(req => !req.isRead && req.status !== 'complete').length;
    const read = requests.filter(req => req.isRead && req.status !== 'complete').length;
    const complete = requests.filter(req => req.status === 'complete').length;
    return {
      all: requests.length,
      pending,
      read,
      complete
    };
  };

  // # Fetch subscription requests on component mount
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const requestsDoc = await getDoc(doc(db, 'technicals', 'sbsc_requests'));
        if (requestsDoc.exists()) {
          // # Convert object to array and sort by creation date
          const requestsArray = Object.entries(requestsDoc.data())
            .filter(([_, data]) => data !== null) // Filter out null entries
            .map(([id, data]) => ({
              id,
              ...data
            }));
          setRequests(requestsArray.sort((a, b) => b.created_at.localeCompare(a.created_at)));
        }
      } catch (error) {
        console.error('Error fetching requests:', error);
      }
    };

    fetchRequests();
  }, []);

  // # Handle request status updates
  const updateRequestStatus = async (requestId, type) => {
    try {
      // # Get current document data
      const requestsDoc = await getDoc(doc(db, 'technicals', 'sbsc_requests'));
      if (!requestsDoc.exists()) return;

      // # Get all current requests
      const currentRequests = requestsDoc.data();
      const currentRequest = currentRequests[requestId];

      // # Determine new status based on type and current state
      let updates = {};
      
      if (type === 'read') {
        if (currentRequest.isRead) {
          // # Unchecking read -> make pending
          updates = {
            isRead: false,
            status: 'pending'
          };
        } else {
          // # Checking read
          updates = {
            isRead: true,
            status: currentRequest.status === 'complete' ? 'complete' : 'read'
          };
        }
      } else if (type === 'complete') {
        if (currentRequest.status === 'complete') {
          // # Unchecking complete -> make read
          updates = {
            isRead: true,
            status: 'read'
          };
        } else {
          // # Checking complete
          updates = {
            isRead: true,
            status: 'complete'
          };
        }
      }

      // # Update the specific request
      const updatedRequest = {
        ...currentRequest,
        ...updates
      };

      // # Create new requests object
      const newRequests = {
        ...currentRequests,
        [requestId]: updatedRequest
      };

      // # Update the entire document
      await setDoc(doc(db, 'technicals', 'sbsc_requests'), newRequests);
      
      // # Update local state
      setRequests(prev => prev.map(req => 
        req.id === requestId ? { ...req, ...updates } : req
      ));

      // # Update selected request if it's open in popup
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => prev ? { ...prev, ...updates } : null);
      }
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  // # Handle request deletion
  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      try {
        // # Get current document data
        const requestsDoc = await getDoc(doc(db, 'technicals', 'sbsc_requests'));
        if (!requestsDoc.exists()) return;

        // # Get all current requests
        const currentRequests = requestsDoc.data();

        // # Remove the specific request
        delete currentRequests[requestId];

        // # Update the entire document
        await setDoc(doc(db, 'technicals', 'sbsc_requests'), currentRequests);
        
        // # Remove from local state
        setRequests(prev => prev.filter(req => req.id !== requestId));
        setSelectedRequest(null);
      } catch (error) {
        console.error('Error deleting request:', error);
      }
    }
  };

  // # Request Details Popup Component
  const RequestDetailsPopup = ({ request, onClose }) => {
    if (!request) return null;
    
    // # Get nationality data if available
    let nationalityInfo = null;
    
    // # Debug nationality data
    console.log('Nationality data in request:', {
      nationality: request.nationality,
      nationalityCode: request.nationalityCode,
      selectedCountry: request.selectedCountry,
      allKeys: Object.keys(request).filter(key => key.toLowerCase().includes('nation') || key.toLowerCase().includes('country'))
    });
    
    // # First try direct lookup by nationality code
    if (request.nationality && typeof request.nationality === 'string') {
      // Direct match with nationality code (e.g., "EGYPT")
      if (nationalityData[request.nationality.toUpperCase()]) {
        nationalityInfo = nationalityData[request.nationality.toUpperCase()];
      }
      // Try to find by nationality name
      else {
        nationalityInfo = findNationalityByName(request.nationality);
      }
    }
    
    // # If not found and selectedCountry exists, try with that
    if (!nationalityInfo && request.selectedCountry) {
      // Try with name_en first
      if (request.selectedCountry.name_en) {
        const matchedNationality = Object.entries(nationalityData).find(([_, data]) => 
          data.name.toLowerCase() === request.selectedCountry.name_en.toLowerCase()
        );
        if (matchedNationality) {
          nationalityInfo = matchedNationality[1];
        }
      }
      
      // If still not found, try with other fields
      if (!nationalityInfo) {
        const countryObj = request.selectedCountry;
        const possibleNames = [
          countryObj.name_en,
          countryObj.name,
          countryObj.name_local,
          countryObj.country,
          countryObj.countryName
        ].filter(Boolean);
        
        for (const name of possibleNames) {
          const matchedNationality = Object.entries(nationalityData).find(([_, data]) => 
            data.name.toLowerCase() === name.toLowerCase() ||
            data.searchKeys.some(key => name.toLowerCase().includes(key.toLowerCase()))
          );
          if (matchedNationality) {
            nationalityInfo = matchedNationality[1];
            break;
          }
        }
      }
    }
    
    // # Last resort: try to find by any country-related field
    if (!nationalityInfo) {
      const countryFields = Object.entries(request).filter(([key, value]) => 
        (key.toLowerCase().includes('nation') || key.toLowerCase().includes('country')) && 
        typeof value === 'string' && value.trim() !== ''
      );
      
      for (const [_, value] of countryFields) {
        const matchedNationality = Object.entries(nationalityData).find(([_, data]) => 
          data.name.toLowerCase() === value.toLowerCase() ||
          data.searchKeys.some(key => value.toLowerCase().includes(key.toLowerCase()))
        );
        if (matchedNationality) {
          nationalityInfo = matchedNationality[1];
          break;
        }
      }
    }

    return (
      <div className="sub_ctrl_popup_overlay" onClick={onClose}>
        <div className="sub_ctrl_popup_content" onClick={e => e.stopPropagation()}>
          <button className="sub_ctrl_popup_close" onClick={onClose}>
            <FaTimes />
          </button>

          <div className="sub_ctrl_popup_header">
            <img src={request.photoURL || '/icons/pfp.png'} alt="Profile" />
            <h2>{request.name}</h2>
            <span className={`sub_ctrl_popup_status ${request.status}`}>
              {request.status || 'pending'}
            </span>
          </div>

          <div className="sub_ctrl_popup_details">
            <div className="sub_ctrl_popup_section">
              <h3>Personal Information</h3>
              <p><FaEnvelope /> {request.email}</p>
              <p><FaPhone /> {request.phoneCode} {request.phoneNumber}</p>
              {nationalityInfo ? (
                <p className="sub_ctrl_nationality">
                  <span className="sub_ctrl_flag">{nationalityInfo.flag}</span>
                  <strong>Nationality:</strong> {nationalityInfo.name}
                </p>
              ) : request.selectedCountry && request.selectedCountry.flag ? (
                <p className="sub_ctrl_nationality">
                  <span className="sub_ctrl_flag">{request.selectedCountry.flag}</span>
                  <strong>Nationality:</strong> {request.selectedCountry.name_en || 'Unknown'}
                </p>
              ) : (
                <p><FaFlag /> <strong>Nationality:</strong> {
                  request.nationality || 
                  request.nationalityCode || 
                  (request.selectedCountry ? 
                    (request.selectedCountry.name_en || 
                     request.selectedCountry.name || 
                     request.selectedCountry.country || 
                     JSON.stringify(request.selectedCountry).replace(/[{}"]/g, '')) : 
                    'Not specified')
                }</p>
              )}
            </div>

            <div className="sub_ctrl_popup_section">
              <h3>Product Details</h3>
              <p><FaBoxOpen /> <strong>Product:</strong> {formatProductName(request.product)}</p>
              <p><FaClock /> <strong>Duration:</strong> {request.duration}</p>
            </div>

            <div className="sub_ctrl_popup_section">
              <h3>Academic Information</h3>
              <p><FaUniversity /> {request.university}</p>
              <p><FaGraduationCap /> <strong>Phase:</strong> {request.phase}</p>
              <p><FaChartLine /> <strong>GPA:</strong> {request.gpa}</p>
            </div>

            <div className="sub_ctrl_popup_section">
              <h3>Request Details</h3>
              <p><FaCalendarAlt /> Created: {new Date(request.created_at).toLocaleString()}</p>
            </div>
          </div>

          <div className="sub_ctrl_popup_actions">
            <button 
              className={`sub_ctrl_action_btn ${request.isRead ? 'active' : ''}`}
              onClick={() => updateRequestStatus(request.id, 'read')}
            >
              <FaEye />
            </button>
            
            <button 
              className={`sub_ctrl_action_btn ${request.status === 'complete' ? 'active' : ''}`}
              onClick={() => updateRequestStatus(request.id, 'complete')}
            >
              <FaCheck />
            </button>
            
            <button 
              className="sub_ctrl_delete_btn"
              onClick={() => handleDelete(request.id)}
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sub_ctrl_container">
      <SharedHeader title="Subscription Controls" />
      
      <div className="sub_ctrl_content">
        <div className="sub_ctrl_title">
          <h1>Subscription Management</h1>
        </div>

        <div className="sub_ctrl_tabs">
          <button 
            className={`sub_ctrl_tab ${activeTab === 'controls' ? 'active' : ''}`}
            onClick={() => setActiveTab('controls')}
          >
            Controls
          </button>
          <button 
            className={`sub_ctrl_tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Requests
          </button>
        </div>

        <div className="sub_ctrl_tab_content">
          {activeTab === 'controls' ? (
            <div className="sub_ctrl_controls">
              <h2>Controls Panel</h2>
              <p>Content coming soon...</p>
            </div>
          ) : (
            <>
              <div className="sub_ctrl_request_filters">
                <button 
                  className={`sub_ctrl_filter_tab ${requestFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setRequestFilter('all')}
                  data-count={getRequestCounts().all}
                >
                  All
                </button>
                <button 
                  className={`sub_ctrl_filter_tab ${requestFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setRequestFilter('pending')}
                  data-count={getRequestCounts().pending}
                >
                  Pending
                </button>
                <button 
                  className={`sub_ctrl_filter_tab ${requestFilter === 'read' ? 'active' : ''}`}
                  onClick={() => setRequestFilter('read')}
                  data-count={getRequestCounts().read}
                >
                  Read
                </button>
                <button 
                  className={`sub_ctrl_filter_tab ${requestFilter === 'complete' ? 'active' : ''}`}
                  onClick={() => setRequestFilter('complete')}
                  data-count={getRequestCounts().complete}
                >
                  Complete
                </button>
              </div>
              <div className="sub_ctrl_requests">
                {getFilteredRequests().map((request) => (
                  <div key={request.id} className={`sub_ctrl_request_tile ${request.status}`}>
                    <div className="sub_ctrl_request_profile" onClick={() => setSelectedRequest(request)}>
                      <img src={request.photoURL || '/icons/pfp.png'} alt="Profile" />
                      <div className="sub_ctrl_request_info">
                        <h3>{request.name}</h3>
                        <p>{formatProductName(request.product)}</p>
                      </div>
                    </div>
                    
                    <div className="sub_ctrl_request_actions">
                      <button 
                        className={`sub_ctrl_action_btn ${request.isRead ? 'active' : ''}`}
                        onClick={() => updateRequestStatus(request.id, 'read')}
                      >
                        <FaEye />
                      </button>
                      
                      <button 
                        className={`sub_ctrl_action_btn ${request.status === 'complete' ? 'active' : ''}`}
                        onClick={() => updateRequestStatus(request.id, 'complete')}
                      >
                        <FaCheck />
                      </button>
                      
                      <button 
                        className="sub_ctrl_delete_btn"
                        onClick={() => handleDelete(request.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      <RequestDetailsPopup 
        request={selectedRequest} 
        onClose={() => setSelectedRequest(null)} 
      />
      
      <DashboardFooter />
    </div>
  );
};

export default SubscriptionControls;

const SubscriptionRequests = () => {}
